import { useCanvasControlScroll } from "../../../src/components/editorViewport/useCanvasControlScroll";
import "../../../src/scss/editorControls.scss";
import { itx } from "../../../src/translationItx/itx";
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type PointerEventHandler,
  type Ref,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import { X } from "lucide-react";
import { createPortal } from "react-dom";

export function useC1DialogDrag(open: boolean, onDragStart?: () => void) {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{ pointerId: number; clientX: number; clientY: number; startX: number; startY: number } | null>(
    null,
  );

  useEffect(() => {
    if (open) setOffset({ x: 0, y: 0 });
  }, [open]);

  const onPointerDown: PointerEventHandler<HTMLElement> = (event) => {
    if (event.button !== 0) return;
    onDragStart?.();
    // Keep title clicks/double-clicks on their original element while drag events bubble to the header.
    const captureTarget = event.target instanceof Element ? event.target : event.currentTarget;
    captureTarget.setPointerCapture(event.pointerId);
    drag.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      startX: offset.x,
      startY: offset.y,
    };
    event.preventDefault();
  };
  const onPointerMove: PointerEventHandler<HTMLElement> = (event) => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    setOffset({
      x: current.startX + event.clientX - current.clientX,
      y: current.startY + event.clientY - current.clientY,
    });
  };
  const finishDrag: PointerEventHandler<HTMLElement> = (event) => {
    if (drag.current?.pointerId === event.pointerId) drag.current = null;
  };

  return {
    dialogStyle: {
      transform: `translate(${offset.x}px, ${offset.y}px)`,
      "--c1-dialog-drag-x": `${offset.x}px`,
      "--c1-dialog-drag-y": `${offset.y}px`,
    } as CSSProperties,
    headerProps: { onPointerDown, onPointerMove, onPointerUp: finishDrag, onPointerCancel: finishDrag },
  };
}

type DialogProps = {
  open: boolean;
  title: ReactNode;
  children: ReactNode;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  busy?: boolean;
  confirmDisabled?: boolean;
  className?: string;
  closeButtonClassName?: string;
  headerClassName?: string;
  contentClassName?: string;
  modal?: boolean;
  showActions?: boolean;
  showConfirm?: boolean;
  closeOnEscape?: boolean;
  onHeaderPointerEnter?: PointerEventHandler<HTMLDivElement>;
  draggable?: boolean;
  onDragStart?: () => void;
  initialPosition?: { left: number; top: number };
  dialogRef?: Ref<HTMLFormElement>;
  headerActions?: ReactNode;
  portal?: boolean;
  noValidate?: boolean;
};

export function C1_Dialog({
  open,
  title,
  children,
  onConfirm,
  onCancel,
  confirmLabel = itx("verificationProjectCard.ok", "OK"),
  cancelLabel = itx("exportGroupAddressesDialog.cancel", "Abbrechen"),
  busy = false,
  confirmDisabled = false,
  className,
  closeButtonClassName,
  headerClassName,
  contentClassName,
  modal = true,
  showActions = true,
  showConfirm = true,
  closeOnEscape = true,
  onHeaderPointerEnter,
  draggable = true,
  onDragStart,
  initialPosition,
  dialogRef,
  headerActions,
  portal = false,
  noValidate = false,
}: DialogProps) {
  const { dialogStyle, headerProps } = useC1DialogDrag(open, onDragStart);
  const scrollRef = useCanvasControlScroll(dialogRef);
  useEffect(() => {
    if (!open || !closeOnEscape || !onCancel) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy && !globalThis.state?.dialogManager?.isOpen) onCancel();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [busy, closeOnEscape, onCancel, open]);

  if (!open) return null;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!busy && !confirmDisabled && showConfirm && onConfirm) void onConfirm();
  };
  const stopPropagation = (event: SyntheticEvent) => event.stopPropagation();

  const dialog = (
    <div className={`C1_dialogBackdrop${modal ? "" : " C1_dialogBackdrop--modeless"}`} role="presentation">
      <form
        noValidate={noValidate}
        ref={scrollRef}
        className={["C1_dialog dockTypography", className].filter(Boolean).join(" ")}
        data-canvas-navigation="ignore"
        role="dialog"
        aria-modal={modal}
        aria-labelledby="dialog-title"
        onSubmit={submit}
        onPointerDown={stopPropagation}
        onPointerUp={stopPropagation}
        onPointerCancel={stopPropagation}
        onMouseDown={stopPropagation}
        onMouseUp={stopPropagation}
        onClick={stopPropagation}
        onDoubleClick={stopPropagation}
        onContextMenu={stopPropagation}
        onWheel={stopPropagation}
        style={{
          ...(initialPosition
            ? { position: "fixed", left: initialPosition.left, top: initialPosition.top }
            : undefined),
          ...(draggable ? dialogStyle : undefined),
        }}
      >
        <div
          className={["C1_dialogHeader", !draggable && "C1_dialogHeader--fixed", headerClassName].filter(Boolean).join(" ")}
          {...(draggable ? headerProps : {})}
          onPointerEnter={onHeaderPointerEnter}
          onPointerMove={(event) => {
            if (draggable) headerProps.onPointerMove(event);
            event.stopPropagation();
          }}
        >
          <h1 id="dialog-title" className="dockTitle">{title}</h1>
          {(headerActions || (!modal && onCancel)) && (
            <div className="C1_dialogHeaderActions">
              {headerActions}
              {!modal && onCancel && (
                <button
                  type="button"
                  className={["C1_dialogClose", closeButtonClassName].filter(Boolean).join(" ")}
                  aria-label={itx("c1.ui.Dialog_schliessen", "Dialog schließen")}
                  title={itx("menuBar.close", "Schließen")}
                  disabled={busy}
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={onCancel}
                >
                  <X size={18} />
                </button>
              )}
            </div>
          )}
        </div>
        <div className={["C1_dialogContent", contentClassName].filter(Boolean).join(" ")}>{children}</div>
        {showActions && (
          <div className="C1_dialogActions">
            {showConfirm && (
              <button type="submit" className="C1_primary" disabled={busy || confirmDisabled}>
                {confirmLabel}
              </button>
            )}
            <button type="button" disabled={busy} onClick={onCancel}>
              {cancelLabel}
            </button>
          </div>
        )}
      </form>
    </div>
  );

  const portalTarget = portal ? (document.querySelector<HTMLElement>(".C1") ?? document.body) : undefined;
  return portalTarget ? createPortal(dialog, portalTarget) : dialog;
}
